// Screen-level layout for the activity solver (Playful Frost re-skin). Prefix: aas-.
// Aggregated into libraryStyles. Owns the white card surface + the classic exam
// layout (user rule): READING PASSAGE pane on the LEFT (collapses via :empty when
// the activity has none), QUESTION on the RIGHT with the ANSWERS BELOW it. The
// per-type interaction (the *MaterialContent, mc-* classes) renders in the answers
// section and is reused verbatim. The passage|content row kicks in at >=980px
// unless the layout is forced to stack (chat open → isFullScreen → --stacked).
export const activitySolveLayoutStyles = `
  .aas-layout {
    height: 100%;
    width: 100%;
    overflow-y: auto;
    background: var(--catchup-white);
    border-radius: var(--rad-catchup-xlarge);
    box-shadow: var(--shadow-card);
    padding: var(--space-6);
  }
  @media (min-width: 768px) { .aas-layout { padding: var(--space-8); } }

  /* single scroll: the whole card scrolls as one unit (matches the design's
     spv-card), not independent panes */
  @media (min-width: 980px) {
    .aas-layout--panes {
      overflow-y: auto;
      display: flex;
      flex-direction: column;
    }
    .aas-layout--panes > .aas-grid { flex: 1 1 0%; min-height: 0; }
  }

  .aas-grid {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    min-height: 100%;
  }

  /* Reading-passage pane — LEFT. The solve screens pass the passage node
     unconditionally; it renders nothing for passage-less activities, so an
     :empty pane (and the divider on its sibling) collapses away. */
  .aas-passcol {
    min-width: 0;
  }
  .aas-passcol:empty { display: none; }

  /* Question + answers column. Stacked by default (question above, answers below
     a divider); becomes a Soru | Cevap row when there's no passage — see the
     >=980px block. */
  .aas-content-col {
    flex: 1 1 0%;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .aas-soru { min-width: 0; }
  /* Inner block of a split pane (question / answer). Purely structural and
     visually inert — it exists so a stretch-sized pane has slack for the pin to
     travel in; see the .aas-pane--pin rule in the >=980px block. */
  .aas-pane-inner { min-width: 0; }
  .aas-body, .aas-inline-body { min-width: 0; }
  .aas-answers {
    min-width: 0;
    border-top: 1px solid var(--catchup-blue-100);
    padding-top: var(--space-5);
    margin-top: var(--space-5);
  }

  /* Labelled pane header (question / answer) — matches the passage OKUMA METNİ pill */
  .aas-pane-head { display: flex; align-items: center; gap: var(--space-3); margin-bottom: var(--space-4); }
  .aas-pane-ic {
    display: flex; align-items: center; justify-content: center;
    width: 34px; height: 34px; flex-shrink: 0;
    border-radius: var(--rad-catchup-medium);
    background: var(--brand-50);
    color: var(--brand-dark);
    box-shadow: inset 0 1px 1px rgba(255,255,255,0.8), 0 2px 6px -2px rgba(15,72,89,0.2);
  }
  .aas-pane-ic svg { display: block; }
  .aas-pane-ic--answer { background: var(--accent-gold-bg); color: var(--accent-gold-text, var(--brand-dark)); }
  .aas-pane-title {
    font-size: var(--fs-md);
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--brand-dark);
  }

  /* >=980px & not stacked by the chat: passage | question-and-answers row.
     The divider between the panes only exists while the passage pane is
     non-empty (display:none does not stop sibling matching — :empty does). */
  @media (min-width: 980px) {
    .aas-grid:not(.aas-grid--stacked) {
      flex-direction: row;
      gap: var(--space-8);
      align-items: stretch;
    }
    .aas-grid:not(.aas-grid--stacked) > .aas-passcol {
      flex: 0 0 40%;
      align-self: flex-start;
    }
    .aas-grid:not(.aas-grid--stacked) > .aas-passcol:not(:empty) + .aas-content-col {
      border-left: 1px solid var(--catchup-blue-100);
      padding-left: var(--space-8);
    }

    /* No passage → split the content column into two panes: SORU | CEVAP.
       Scoped by the collapsed (:empty) passage pane the solve screens always
       render, so preview surfaces that pass no passage keep their single column,
       and a present passage (:not(:empty)) keeps the stacked question-over-answer
       column. "inline" (Dropdown / FillInTheBlanks) never splits. */
    .aas-grid:not(.aas-grid--stacked) > .aas-passcol:empty + .aas-content-col--split,
    .aas-grid:not(.aas-grid--stacked) > .aas-passcol:empty + .aas-content-col--wide {
      flex-direction: row;
      gap: var(--space-8);
      align-items: stretch;
    }
    /* The answer pane's divider turns vertical (a rule between the panes) instead
       of the horizontal top border it carries when stacked. */
    .aas-grid:not(.aas-grid--stacked) > .aas-passcol:empty + .aas-content-col--split > .aas-answers,
    .aas-grid:not(.aas-grid--stacked) > .aas-passcol:empty + .aas-content-col--wide > .aas-answers {
      border-top: none;
      margin-top: 0;
      padding-top: 0;
      border-left: 1px solid var(--catchup-blue-100);
      padding-left: var(--space-8);
    }
    /* Ratios — question-weighted for short list answers (MCSA/MCMA), answer-
       weighted for interactions that want the room (True-False, Open-ended,
       Matching, Grouping, Ordering). */
    .aas-grid:not(.aas-grid--stacked) > .aas-passcol:empty + .aas-content-col--split > .aas-soru { flex: 55 1 0%; min-width: 0; }
    .aas-grid:not(.aas-grid--stacked) > .aas-passcol:empty + .aas-content-col--split > .aas-answers { flex: 45 1 0%; min-width: 0; }
    .aas-grid:not(.aas-grid--stacked) > .aas-passcol:empty + .aas-content-col--wide > .aas-soru { flex: 40 1 0%; min-width: 0; }
    .aas-grid:not(.aas-grid--stacked) > .aas-passcol:empty + .aas-content-col--wide > .aas-answers { flex: 60 1 0%; min-width: 0; }

    /* Pane pinning (opt-in per template via ActivitySolveLayout's paneFit prop —
       currently MCSA / MCMA / True-False, whose answer pane does not change height
       as the learner works in it). Either side can be the tall one, so BOTH panes
       are candidates and the shorter one selects itself: the panes are stretch-
       sized to the taller one, .aas-pane-inner keeps its content height, and that
       gap is the only room a sticky element can travel in — so the tall pane's
       inner block (which fills its pane exactly) is a no-op. .aas-pane--pin is
       added by usePaneFitMode only when the block also FITS the scrollport,
       because a sticky element taller than its scrollport freezes with its lower
       half unreachable — for a learner, an answer they cannot scroll to. */
    .aas-grid:not(.aas-grid--stacked) > .aas-passcol:empty + .aas-content-col--split > .aas-pane--pin > .aas-pane-inner,
    .aas-grid:not(.aas-grid--stacked) > .aas-passcol:empty + .aas-content-col--wide > .aas-pane--pin > .aas-pane-inner {
      position: sticky;
      /* Resolved against the real scroller, which is NOT .aas-layout — every solve
         surface overrides that to overflow:visible and scrolls an outer pane
         instead (see "WHERE THE SCROLLPORT ACTUALLY IS" in usePaneFitMode.ts).
         Those panes carry no padding of their own, so 0 sticks the block flush
         with the top of the scroll viewport. */
      top: 0;
    }

    /* Open-ended in the no-passage split: the soru pane already bounds the image,
       so keep it a plain full-width block — the newspaper float (from the OE rule
       in mediaStyles) would only leave an unreadably thin text column beside it in
       this narrow pane. The float still applies in the full-width/mobile and
       passage cases, where the column is wide enough to wrap. */
    .aas-grid:not(.aas-grid--stacked) > .aas-passcol:empty + .aas-content-col--wide .am-body-list--oe .med-img-wrap.med-pimg-float {
      float: none;
      width: 100%;
      /* The float rule caps the figure at 44%; without this reset the image
         stays half-width with the text stacked below it. */
      max-width: none;
      margin: 0.15rem 0 0.5rem 0;
    }
  }

  /* In-sentence inline controls (Dropdown / FillInTheBlanks) */
  .aas-inline-prompt {
    font-size: var(--fs-lg);
    font-weight: 600;
    color: var(--text-secondary);
    margin-bottom: var(--space-3);
  }
  /* Block flow (not a flex column) so a floated body image lets the sentence
     wrap around it; container-type makes that float width-aware via @container.
     Line spacing that the flex gap used to provide is restored with margins. */
  .aas-inline-sentence { display: block; container-type: inline-size; container-name: pac; }
  .aas-inline-sentence::after { content: ""; display: block; clear: both; }
  .aas-inline-sentence > .aas-inline-line { margin: 0 0 var(--space-4); }
  .aas-inline-sentence > .aas-inline-line:last-child { margin-bottom: 0; }
  /* Newspaper figure-float for a Dropdown / FillInTheBlanks body image: it
     floats left and the sentence (with its in-line controls) wraps around it,
     filling the full-width row instead of leaving the space beside a centered
     image empty. Below the threshold it falls back to a full-width block. */
  .aas-inline-sentence .med-img-wrap.med-pimg-full { width: 100%; }
  @container pac (min-width: 520px) {
    .aas-inline-sentence .med-img-wrap.med-pimg-float {
      display: block;
      float: left;
      /* Hug the image (bounded at 44%) so a portrait figure capped by height
         doesn't leave an empty gap between it and the wrapping sentence. */
      width: fit-content;
      max-width: 44%;
      margin: 0.15rem 1.25rem 0.5rem 0;
    }
    .aas-inline-sentence .med-pimg-float .med-img-inner { max-height: 42vh; }
  }
  .aas-blank-list { display: flex; flex-direction: column; gap: var(--space-3); margin-top: var(--space-2); }
  .aas-blank-row { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; }
  .aas-blank-num { font-size: var(--fs-lg); font-weight: 700; color: var(--brand-dark); }
  .aas-inline-line {
    font-size: var(--fs-xl);
    font-weight: 500;
    color: var(--text-primary);
    line-height: 2.4;
    white-space: pre-wrap;
  }
  .aas-blank-wrap { display: inline-flex; align-items: center; vertical-align: middle; }

  /* inline dropdown select — V4 custom glass popover (aas__inselect) */
  .aas-insel-wrap { position: relative; display: inline-flex; align-items: center; vertical-align: middle; margin: 0 2px; }
  .aas-insel {
    display: inline-flex; align-items: center; gap: 5px;
    font-family: var(--font-sans); font-size: var(--fs-lg); font-weight: 700; cursor: pointer;
    padding: 2px var(--space-3);
    color: var(--brand-dark);
    border: 1.5px solid var(--catchup-blue-200); border-radius: var(--rad-catchup-full);
    background: var(--catchup-white); box-shadow: var(--shadow-card);
    vertical-align: middle;
    transition: border-color var(--duration-base) var(--ease-standard), box-shadow var(--duration-base) var(--ease-standard);
  }
  .aas-insel:hover { border-color: var(--catchup-blue); }
  .aas-insel.is-open { border-color: var(--catchup-blue); box-shadow: 0 0 0 3px var(--brand-100, rgba(87,194,211,0.3)); }
  .aas-insel.is-filled { background: var(--brand-50); }
  .aas-insel.is-correct { border-color: var(--accent-lime-text); background: var(--accent-lime-bg, #f1f9e3); color: var(--accent-lime-text); cursor: default; }
  .aas-insel.is-wrong { border-color: var(--accent-coral); background: var(--accent-coral-bg); color: var(--accent-coral-text); cursor: default; }
  /* Left blank — amber, deliberately NOT the coral of a wrong pick: the learner
     did not answer wrongly, they did not answer. The key still shows below. */
  .aas-insel.is-blank { border-color: var(--color-warning); background: var(--color-warning-bg); color: var(--color-warning-text); cursor: default; }
  .aas-insel-ph { color: var(--text-muted); font-weight: 600; }
  .aas-insel.is-blank .aas-insel-ph { color: var(--color-warning-text); font-weight: 700; }
  .aas-insel-caret { font-size: 10px; opacity: 0.6; }
  .aas-insel-menu {
    position: absolute; top: calc(100% + 6px); left: 0; z-index: calc(var(--z-dropdown, 200) + 1); min-width: 100%;
    display: flex; flex-direction: column; gap: 2px; padding: var(--space-2);
    background: var(--catchup-white); border: 1px solid var(--catchup-blue-100); border-radius: var(--rad-catchup-medium);
    box-shadow: 0 12px 28px -10px rgba(15,72,89,0.35);
    /* Always scrollable so a long option list is reachable even in tight space. */
    max-height: min(280px, 40vh); overflow-y: auto;
  }
  /* Flip above the trigger when it sits near the viewport bottom. */
  .aas-insel-menu.is-up { top: auto; bottom: calc(100% + 6px); }
  .aas-insel-opt {
    font-family: var(--font-sans); font-size: var(--fs-base); font-weight: 600; text-align: left; white-space: nowrap; cursor: pointer;
    padding: var(--space-2) var(--space-3); border: none; background: transparent; color: var(--text-primary); border-radius: var(--rad-catchup-small);
  }
  .aas-insel-opt:hover { background: var(--brand-50); }
  .aas-insel-opt.is-on { background: var(--catchup-blue); color: #fff; }

  /* compact stacked "Doğru" chip (V4 aas__corr) — the wrong-answer reveal */
  .aas-astack { display: inline-flex; flex-direction: column; align-items: flex-start; gap: 3px; vertical-align: top; max-width: 100%; }
  .aas-corr {
    display: inline-flex; flex-wrap: wrap; align-items: baseline; gap: 4px 6px; max-width: 100%; margin-top: 1px;
    padding: 3px var(--space-2); border-radius: var(--rad-catchup-small); background: var(--color-success-bg);
  }
  .aas-corr-tag { font-size: 9px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-success-text); opacity: 0.85; }
  .aas-corr-val { font-size: var(--fs-sm); font-weight: 800; color: var(--color-success-text); line-height: 1.35; overflow-wrap: anywhere; word-break: break-word; white-space: normal; }

  /* FillInTheBlanks — V4 inline slot (aas__fb-slot) */
  .aas-fbslot {
    display: inline-flex; align-items: center; gap: 4px; vertical-align: middle; cursor: pointer;
    font-family: var(--font-sans); font-size: var(--fs-lg); font-weight: 700; color: var(--brand-dark);
    margin: 0 4px; padding: 4px var(--space-3); min-width: 92px; justify-content: center;
    border: none; border-bottom: 2.5px solid var(--catchup-blue-200);
    background: var(--catchup-blue-50, rgba(87,194,211,0.08));
    border-radius: var(--rad-catchup-small) var(--rad-catchup-small) 6px 6px;
    box-sizing: border-box;
    transition: background var(--duration-base) var(--ease-standard), border-color var(--duration-base) var(--ease-standard), box-shadow var(--duration-base) var(--ease-standard);
  }
  .aas-fbslot.is-empty { color: var(--text-muted); }
  .aas-fbslot:hover { border-bottom-color: var(--catchup-blue); background: var(--brand-50); }
  .aas-fbslot.is-target { border-bottom-color: var(--catchup-blue); background: var(--brand-50); box-shadow: 0 0 0 3px var(--brand-100, rgba(87,194,211,0.22)); }
  .aas-fbslot.is-filled { color: var(--brand-dark); border-bottom-style: solid; border-bottom-color: var(--catchup-blue); background: var(--brand-50); }
  .aas-fbph { font-weight: 500; font-size: var(--fs-md); opacity: 0.7; }
  .aas-fbx { font-size: var(--fs-md); font-weight: 700; color: var(--text-muted); opacity: 0.65; }
  .aas-fbslot:hover .aas-fbx { opacity: 1; color: var(--accent-coral-text); }
  .aas-fbic { font-size: var(--fs-sm); font-weight: 800; }
  .aas-fbslot.is-correct {
    color: var(--color-success-text); font-weight: 800; cursor: default;
    border-bottom: 2px solid var(--color-success);
    background: linear-gradient(160deg, rgba(122,203,164,0.36), rgba(94,170,130,0.22));
    border-radius: var(--rad-catchup-small);
    box-shadow: 0 0 0 2px var(--color-success-ring, rgba(70,150,95,0.25));
  }
  .aas-fbslot.is-wrong {
    color: var(--color-error-text); font-weight: 800; cursor: default;
    border-bottom: 2px solid var(--color-error);
    background: var(--color-error-bg);
    border-radius: var(--rad-catchup-small);
    box-shadow: 0 0 0 2px var(--color-error-ring, rgba(217,97,63,0.18));
  }
  .aas-fbslot.is-correct .aas-fbic { color: var(--color-success-text); }
  .aas-fbslot.is-wrong .aas-fbic { color: var(--color-error-text); }
  /* Left blank — amber, deliberately NOT the red of a wrong word: the learner
     did not answer wrongly, they did not answer. The key still shows below. */
  .aas-fbslot.is-blank {
    color: var(--color-warning-text); font-weight: 800; cursor: default;
    border-bottom: 2px solid var(--color-warning);
    background: var(--color-warning-bg);
    border-radius: var(--rad-catchup-small);
  }

  /* word bank — V4 glass card (aas__fb-bank) */
  .aas-bank {
    display: flex; flex-direction: column; gap: var(--space-3);
    padding: var(--space-5); border-radius: var(--rad-catchup-large);
    background: var(--glass-strip-bg);
    -webkit-backdrop-filter: var(--glass-strip-filter); backdrop-filter: var(--glass-strip-filter);
    border: 1px solid var(--glass-border-top); box-shadow: var(--glass-strip-shadow);
  }
  .aas-bank-head { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); }
  .aas-bank-label { font-size: var(--fs-sm); font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: var(--brand-dark); }
  .aas-bank-count { font-size: var(--fs-sm); font-weight: 700; color: var(--text-muted); }
  .aas-bank-chips { display: flex; flex-wrap: wrap; gap: var(--space-2); }
  .aas-word {
    font-family: var(--font-sans); font-size: var(--fs-base); font-weight: 700; color: var(--brand-dark); cursor: pointer;
    padding: var(--space-2) var(--space-4); border-radius: var(--rad-catchup-full);
    background: var(--catchup-white); border: 1.5px solid var(--catchup-blue-200); box-shadow: var(--shadow-card);
    transition: transform var(--duration-fast, .12s) var(--ease-standard), box-shadow var(--duration-base) var(--ease-standard), background var(--duration-base) var(--ease-standard);
  }
  .aas-word:hover:not(:disabled) { transform: translateY(-1px); background: var(--brand-50); border-color: var(--catchup-blue); box-shadow: 0 6px 14px -6px rgba(15,72,89,0.3); }
  .aas-word:active:not(:disabled) { transform: translateY(0); }
  .aas-word.is-used { opacity: 0.35; text-decoration: line-through; cursor: default; box-shadow: none; }
  .aas-bank-hint { font-size: var(--fs-sm); color: var(--text-muted); margin: 0; }
`;
